import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioSolicitudesGridRow from './InventarioSolicitudesGridRow.jsx';
import InventarioSolicitudesGridFiltros from './InventarioSolicitudesGridFiltros.jsx';
import InventarioSolicitudDespachoModal from './InventarioSolicitudDespachoModal.jsx';
import InventarioSolicitudCancelarModal from './InventarioSolicitudCancelarModal.jsx';
import InventarioSolicitudGestionesModal from './InventarioSolicitudGestionesModal.jsx';
import InventarioSolicitudesMasivaModal, {
  ACCION_DESPACHAR,
  ACCION_RECHAZAR,
} from './InventarioSolicitudesMasivaModal.jsx';
import {
  buildInventarioSolicitudesFilterFields,
  getInventarioSolicitudesFilterDefaults,
} from './inventarioSolicitudesFilterFields.js';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import GridColumnSelector from '../../commons/grid/GridColumnSelector.jsx';
import useColumnVisibility from '../../commons/grid/useColumnVisibility.js';
import {
  columnasDeVista,
  INVENTARIO_SOLICITUDES_COLUMNAS,
  INVENTARIO_SOLICITUDES_COLUMNAS_STORAGE_KEY,
} from './inventarioSolicitudesColumns.js';
import { TIPO_ALMACEN } from './inventarioUbicaciones.js';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/**
 * VEC-3317 — grilla del submódulo de Solicitudes de Stock: Pendientes y Finalizadas.
 *
 * Una solicitud es el pedido que un ticket le hace a un depósito. Esta es la vista de trabajo del
 * Bodeguero: qué repuestos le pidieron, de qué ticket, en qué depósito y cuántos quedan por
 * entregar.
 *
 * TRES DECISIONES DE PRESENTACIÓN:
 *
 *  1. GRANO LÍNEA. Un pedido de tres repuestos son tres filas. Lo que se lista es "qué me pidieron",
 *     no "qué documentos entraron", y el Bodeguero despacha ítem por ítem.
 *  2. LAS DOS VISTAS SON PESTAÑAS, NO UN FILTRO DEL PANEL. Es la pantalla en la que estás: si
 *     viviera en el panel, "Restaurar filtros" te cambiaría de pestaña y la franja de resultados
 *     diría que hay una búsqueda activa por estar mirando finalizadas. El endpoint las distingue con
 *     `?vista=`, así que las dos comparten URL y una sola llamada.
 *  3. SIN ACCIONES DE ESCRITURA. Confirmar el despacho y registrar una devolución son VEC-3318
 *     —esta card es solo lectura, decisión de alcance—, y el aviso de arriba de la tabla lo dice.
 *     NO se deja el botón puesto y deshabilitado: MUI no muestra tooltip sobre un control
 *     deshabilitado (`GridToolbarButton` ni siquiera lo envuelve), así que quedaba un icono mudo que
 *     no hacía nada ni se podía entender. Cuando VEC-3318 traiga la acción, traerá su botón.
 *
 * ORDENAMIENTO POR COLUMNA: abre por fecha descendente —lo último pedido primero, que es el orden
 * con el que se trabaja— y las nueve columnas de datos son clickeables. Tres de ellas (depósito,
 * pendiente, solicitante) el backend las resuelve con expresiones y no con columnas, pero desde acá
 * se declaran igual que el resto.
 */

const BASE_URL = '/inventario/solicitudes/grid';

/**
 * Cuántas columnas ocupa una fila de estado (la de "cargando" y la de "sin resultados"), que van con
 * `colSpan`. Se deriva de las visibles y no es una constante: con una constante, al ocultar columnas
 * el `colSpan` quedaría más largo que la tabla y la fila se desbordaría.
 */
const columnasVisibles = (esVisible) =>
  INVENTARIO_SOLICITUDES_COLUMNAS.filter((c) => esVisible(c.name)).length;

// El default de `GridPagination` es la constante compartida `rowsPerPage` = [10,25,50,100], que NO
// incluye el 5. Se pasa la lista propia para ofrecer las mismas opciones que el resto del módulo.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

// El endpoint topea `perPage` en 100 (ItemStockSolicitudController::PER_PAGE_MAX).
const PAGINATION_INIT = { page: 0, perPage: 10 };

/** Hoy el único tipo de ubicación es el depósito del pañol. Ver el comentario en la grilla de stock. */

/**
 * Una clave por columna ordenable, con la dirección vigente. Los nombres son los que espera el
 * endpoint, que les agrega el sufijo `Sort` (`&fechaSort=desc`), y salen de la whitelist del
 * Repository (`ItemStockSolicitudRepository::CAMPOS_ORDENABLES`).
 *
 * `fecha` arranca en `desc` EXPLÍCITO y no vacío: el backend ya ordena así cuando no le mandan nada
 * (lo último pedido primero), y declararlo acá hace que el indicador de la columna coincida con el
 * orden que se está viendo desde el primer render, en vez de mostrar las tres flechitas neutras
 * sobre un listado que sí está ordenado.
 *
 * Están las DIEZ columnas menos la de acciones. Tres de ellas (depósito, pendiente, solicitante) no
 * son columnas de la base sino expresiones, y el Repository las ordena con `orderByRaw`; desde acá
 * no se nota la diferencia. Lo que NO hay que hacer es declarar una clave que el backend no tenga en
 * su whitelist: mostraría una flecha que promete un orden que no pasa.
 */
const SORT_QUERY_INIT = {
  id: '',
  fecha: 'desc',
  ticket: '',
  numeroDeParte: '',
  descripcion: '',
  deposito: '',
  cantidad: '',
  despachado: '',
  disponible: '',
  pendiente: '',
  fechaCierre: '',
  solicitante: '',
};

/**
 * El morado del encabezado de la tabla (`StyledTableCell.head`, commons/utils/TableStyles.js).
 *
 * Se repite acá y no se importa porque allá es un valor dentro de un `withStyles` de MUI, no una
 * constante exportada. Si alguna vez se extrae, este es el lugar a cambiar: la solapa activa tiene
 * que ser EXACTAMENTE el mismo color para que se lea pegada al encabezado.
 */
const COLOR_ENCABEZADO = '#241b3e';

/** Gris del cuerpo de la tabla. La píldora lo usa cuando no hay agrupación vigente. */
const COLOR_NEUTRO = '#6B6F82';

/**
 * Las dos vistas se ven como SOLAPAS de una misma tabla, no como botones sueltos: la activa toma el
 * color del encabezado y se funde con él, de modo que la grilla parece continuar hacia arriba.
 *
 * Todo va inline y no en `vec-datatables.css` a propósito: ese archivo lo comparten ~37 grillas y un
 * override de `.nav-tabs` ahí les cambiaría las solapas a todas (Adjuntos de Facturas, Tickets,
 * Personas…). Acá el alcance es esta pantalla y nada más.
 */
const SOLAPA_BASE = {
  cursor: 'pointer',
  border: '1px solid #BABFC7',
  borderBottom: 'none',
  borderTopLeftRadius: '0.25rem',
  borderTopRightRadius: '0.25rem',
  marginRight: 2,
  paddingInline: 20,
  fontFamily: "'Poppins', sans-serif",
  fontSize: 14,
};

const SOLAPA_ACTIVA = {
  ...SOLAPA_BASE,
  backgroundColor: COLOR_ENCABEZADO,
  borderColor: COLOR_ENCABEZADO,
  color: '#fff',
  fontWeight: 500,
};

/**
 * Cancela el `margin-bottom: -1px` que Bootstrap le pone a `.nav-tabs .nav-item`. Ver el punto 3 del
 * comentario del `<ul>`: sin esto la solapa se monta 1px sobre el encabezado de la tabla y en la
 * inactiva ese píxel gris se ve como un desnivel.
 */
const SOLAPA_ITEM = { marginBottom: 0 };

const SOLAPA_INACTIVA = {
  ...SOLAPA_BASE,
  backgroundColor: '#f4f5fa',
  color: '#6B6F82',
  fontWeight: 400,
};

/**
 * AGRUPACIÓN — un modo de visualización que CAMBIA LA UNIDAD DE PAGINACIÓN.
 *
 * Agrupar hace dos cosas, y la segunda es la que importa:
 *
 *  1. Pide el orden del grupo, para que las filas del mismo ticket (o ítem, o depósito) queden
 *     pegadas y se puedan dibujar como bloques. `campo` es la clave de orden que viaja, una de las
 *     que el endpoint ya acepta para el click en el encabezado.
 *  2. Manda `agruparPor`, y entonces el backend pagina por GRUPO: una página son N tickets con
 *     TODAS sus líneas, no N líneas sueltas.
 *
 * Lo segundo se agregó porque sin eso un ticket de 16 repuestos entraba 10 en la página 1 y 6 en la
 * 2, y se dibujaba como DOS bloques con el mismo título, cada uno con su propio conteo y su propio
 * colapso. Paginando por grupo, un bloque nunca se parte.
 *
 * EL ORDEN SE PIDE AL SERVIDOR Y NO SE RESUELVE ACÁ, aunque los bloques sí se dibujen acá: reordenar
 * en el front solo alcanzaría a las filas ya traídas.
 *
 * `campo` es la clave de orden que viaja; `clave` es de qué dato depende el corte de bloque (la
 * IDENTIDAD, no el texto: dos ítems distintos con la misma descripción son dos bloques, no uno).
 */
const AGRUPACIONES = {
  ninguna: null,
  ticket: {
    campo: 'ticket',
    clave: (row) => (row.ticket ? row.ticket.id : null),
    titulo: (row, intl) =>
      intl.formatMessage(
        { id: 'inventarioSolicitudes.grid.grupo_ticket', defaultMessage: 'Ticket {n}' },
        { n: row.ticket ? row.ticket.id : '-' }
      ),
  },
  item: {
    campo: 'descripcion',
    clave: (row) => (row.item ? row.item.id : null),
    titulo: (row) => {
      const item = row.item || {};
      return item.numeroDeParte ? item.numeroDeParte + ' · ' + (item.descripcion || '') : item.descripcion || '-';
    },
  },
  deposito: {
    campo: 'deposito',
    // La clave lleva el TIPO además del id: la ubicación es polimórfica, así que el almacén 6
    // y la base 6 son cosas distintas. El backend ya agrupa por el par (ubicacion_type,
    // ubicacion_id) — cortando solo por id, acá se fusionarían en un bloque los grupos que la
    // API devolvió separados.
    clave: (row) => (row.ubicacion ? row.ubicacion.type + ':' + row.ubicacion.id : null),
    titulo: (row) => (row.ubicacion && row.ubicacion.nombre ? row.ubicacion.nombre : '-'),
  },
};

/** Las dos pestañas. Los valores son los que acepta `?vista=` del endpoint. */
const VISTA_PENDIENTES = 'pendientes';
const VISTA_FINALIZADAS = 'finalizadas';

/**
 * El orden con el que abre CADA pestaña, que no es el mismo.
 *
 *  - **Pendientes**: por fecha del pedido descendente — lo último que pidieron, primero. Es la cola
 *    de trabajo del bodeguero.
 *  - **Finalizadas**: por fecha de CIERRE descendente — lo que se resolvió recién, primero. Ahí la
 *    fecha del pedido dice poco: una solicitud vieja puede haberse cerrado hoy, y con el orden de
 *    Pendientes quedaría enterrada al final.
 *
 * Cambiar de pestaña REEMPLAZA el orden en vez de conservarlo, y es a propósito: las dos columnas
 * solo existen en su vista (ver `columnasDeVista`), así que conservar `fechaCierreSort` al volver a
 * Pendientes dejaría la grilla ordenada por una columna que ahí no se ve y vale null en todas.
 */
/**
 * El filtro de ubicación, en el formato de PARES que espera el endpoint.
 *
 * ⚠️ TOLERA EL FORMATO VIEJO, y no es defensa de más: este filtro se persiste en `localStorage`, así
 * que cualquiera que haya elegido un depósito ANTES de que existieran las bases tiene guardado un id
 * pelado ("6", o "3,6"). Mandado tal cual sería `ubicacion=6`, que el backend rechaza con 400: la
 * grilla se rompería en la primera carga después de deployar, solo para quien ya usó la pantalla, y
 * sin ninguna pista de por qué.
 *
 * Un valor sin `:` se interpreta como almacén, que es lo único que existía cuando se guardó. Es el
 * mismo criterio —y el mismo motivo— que `inventarioUbicaciones.parsear()`.
 */
const normalizarUbicaciones = (valor) =>
  String(valor)
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '')
    .map((v) => (v.indexOf(':') === -1 ? TIPO_ALMACEN + ':' + v : v))
    .join(',');

const ordenPorDefecto = (vista) => {
  const query = { ...SORT_QUERY_INIT };
  Object.keys(query).forEach((k) => {
    query[k] = '';
  });
  query[vista === VISTA_FINALIZADAS ? 'fechaCierre' : 'fecha'] = 'desc';
  return query;
};

/**
 * El query por defecto, con las MISMAS reglas de serialización que aplica el panel.
 *
 * Acá el default es VACÍO (a diferencia del historial de movimientos, que abre con los últimos 30
 * días): esta pantalla la acota la pestaña, no el tiempo. Se calcula igual con los helpers del panel
 * en vez de escribir `{}` a mano, para que el día que se decida abrir con un período la franja de
 * resultados siga comparando contra lo correcto.
 */
const buildFilterQueryInit = () => {
  const fields = buildInventarioSolicitudesFilterFields();
  const estado = Object.assign({}, buildEmptyFilterState(fields), getInventarioSolicitudesFilterDefaults());
  return serializeFilterState(fields, estado);
};

const InventarioSolicitudesGrid = () => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [vista, setVista] = useState(VISTA_PENDIENTES);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState(() => buildFilterQueryInit());

  const [sortQuery, setSortQuery] = useState(() => ordenPorDefecto(VISTA_PENDIENTES));
  const [agrupacion, setAgrupacion] = useState('ninguna');

  // Los grupos ABIERTOS, por su clave. Se guarda lo ABIERTO y no lo cerrado justamente para que el
  // default sea "todo colapsado": al elegir una agrupación la lista arranca vacía y los bloques se
  // ven cerrados, que es lo pedido — primero el panorama de los grupos, y el detalle se abre a
  // demanda. Invertir esto (guardar los cerrados) devolvería el default contrario.
  const [gruposAbiertos, setGruposAbiertos] = useState([]);

  /**
   * Los ids de detalle tildados, para las acciones masivas.
   *
   * ACUMULA ENTRE PÁGINAS: si se tilda algo en la página 1 y se pasa a la 2, lo de la 1 sigue
   * elegido. Es lo que se decidió para Facturas en VEC-3718, y es lo que uno espera de una selección
   * que después va a disparar una acción sobre todo lo elegido.
   *
   * Se limpia cuando cambia el CONJUNTO de datos —la pestaña o los filtros—, porque ahí lo tildado
   * puede ya no existir; no se limpia al paginar, ordenar ni agrupar, que son la misma data mostrada
   * de otra forma.
   */
  const [seleccionados, setSeleccionados] = useState([]);

  // Qué columnas eligió ver el usuario. El hook persiste las OCULTAS por nombre en localStorage, así
  // que sobrevive al refresh y no se corre si algún día se agrega o saca una columna.
  // Las columnas dependen de la PESTAÑA: Pendiente solo tiene sentido en Pendientes, y la fecha de
  // cierre solo en Finalizadas. El hook recalcula todo desde este array en cada render, así que
  // pasarle uno distinto por vista alcanza; lo que el usuario eligió ocultar se persiste por nombre
  // y sobrevive al cambio de pestaña.
  const columnas = useColumnVisibility(
    columnasDeVista(vista),
    INVENTARIO_SOLICITUDES_COLUMNAS_STORAGE_KEY
  );
  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);

  // La línea sobre la que se está actuando, o null. Un estado por modal y no uno compartido con un
  // discriminante: son dos formularios distintos y mezclarlos obliga a preguntar cuál es en cada
  // render.
  const [aDespachar, setADespachar] = useState(null);
  const [aCancelar, setACancelar] = useState(null);
  // La línea cuyo historial se está mirando. Es de solo lectura, así que no comparte estado con las
  // otras dos ni dispara el refetch al cerrarse.
  const [aVerGestiones, setAVerGestiones] = useState(null);

  // La acción masiva en curso (`despachar` / `rechazar`), o null.
  const [accionMasiva, setAccionMasiva] = useState(null);

  // Se incrementa al gestionar para forzar el refetch: despachar cambia el pendiente y puede cerrar
  // la línea, así que la página que se está viendo quedó vieja.
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    // Guarda de secuencia: el `useEffect` puede dejar DOS pedidos en vuelo y sin esto pinta el que
    // responda último, no el último que se pidió. Pasa en cada mount con filtros persistidos: el
    // efecto arranca con el query por defecto (`filterQuery` se inicializa sin leer localStorage) y
    // apenas el panel se hidrata dispara `onApply`, que cambia las deps y lanza el segundo. También
    // cubre el cambio rápido de pestaña, que es la carrera más fácil de provocar acá.
    // El cleanup corre cuando cambian las deps, así que marca vencido todo pedido anterior.
    let vigente = true;

    setLoading(true);
    if (
      Security.hasPermission('INVENTARIO_SOLICITUDES_LISTAR') &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      fetchRows(() => vigente);
    } else {
      setRedirectTo('/error');
    }
    return () => {
      vigente = false;
    };
  }, [pagination, filterQuery, vista, sortQuery, agrupacion, recarga]);

  /** `sigueVigente` lo provee el efecto que disparó el pedido. Ver la guarda de secuencia. */
  const fetchRows = (sigueVigente) => {
    Utils.getData(`${BASE_URL}${getQuery()}`)
      .then((data) => {
        if (!sigueVigente()) {
          return; // llegó una respuesta vencida: la pidió un filtro o una pestaña que ya no es la vigente
        }
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => {
        if (!sigueVigente()) {
          return;
        }
        setLoading(false);
      });
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;
    query += '&vista=' + encodeURIComponent(vista);

    // Agrupar no filtra: le dice al backend que pagine por GRUPO. Sin esto la página son 10 líneas
    // y un ticket de 16 repuestos se parte en dos bloques, uno por página.
    if (AGRUPACIONES[agrupacion]) query += '&agruparPor=' + encodeURIComponent(agrupacion);

    // La ubicación viaja como lista de PARES ("base:1161,almacen:6") y no como un tipo con N ids.
    //
    // Desde VEC-3316 el stock vive en almacenes del Pañol Y en bases, así que el multiselect mezcla
    // los dos tipos: un id suelto ya no identifica nada —el 6 puede ser el almacén 6 o la base 6— y
    // un único `ubicacionType` no puede describir una selección mixta. El valor de cada opción ya
    // trae su tipo (ver inventarioUbicaciones), así que se manda tal cual.
    if (filterQuery.ubicacionId) {
      query += '&ubicacion=' + encodeURIComponent(normalizarUbicaciones(filterQuery.ubicacionId));
    }

    query += '&itemBusqueda=' + encodeURIComponent(filterQuery.itemBusqueda || '');

    // Solo los dígitos: el panel no tiene campo numérico y el endpoint valida entero positivo, así
    // que un "T-123" escrito a mano daría 400 en vez de una búsqueda. Vacío se manda vacío.
    const ticket = String(filterQuery.ticket || '').replace(/\D/g, '');
    query += '&ticket=' + encodeURIComponent(ticket);

    // Mismo trato que el ticket: solo dígitos, porque el endpoint valida entero positivo.
    const id = String(filterQuery.id || '').replace(/\D/g, '');
    query += '&id=' + encodeURIComponent(id);

    // Las dos claves del período. Vacías es "todo el historial", y el backend NO aplica ninguna
    // ventana por defecto: una solicitud pendiente vieja sigue siendo trabajo por hacer.
    query += '&fechaDesde=' + encodeURIComponent(filterQuery.fechaDesde || '');
    query += '&fechaHasta=' + encodeURIComponent(filterQuery.fechaHasta || '');

    // Solo viaja la columna que tiene dirección: el endpoint toma la PRIMERA con valor
    // (GridSortHelper) y mandar las vacías sería ruido en la URL.
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      if (sortQuery[k]) query += '&' + k + 'Sort=' + sortQuery[k];
    });

    return query;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  /**
   * Elegir una agrupación es elegir un orden: se manda la clave de orden del grupo y se limpian las
   * demás, igual que un click en el encabezado. "Sin agrupar" vuelve al orden por defecto.
   *
   * Las dos formas de ordenar conviven sin estado contradictorio porque son la MISMA: agrupar por
   * ticket deja marcada la flecha de la columna Ticket, que es exactamente lo que está pasando.
   */
  const handleAgrupacion = (modo) => {
    if (modo === agrupacion) return;

    const config = AGRUPACIONES[modo];
    const query = { ...SORT_QUERY_INIT };
    if (config) {
      Object.keys(query).forEach((k) => {
        query[k] = '';
      });
      query[config.campo] = 'asc';
    }

    setAgrupacion(modo);
    setGruposAbiertos([]);
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSortQuery(query);
  };

  /**
   * Un click en el encabezado. Ordena por ESA columna y limpia las demás: el endpoint ordena por una
   * sola (toma la primera con valor), así que dejar dos marcadas mostraría dos flechas cuando solo
   * una manda.
   *
   * Primer click en una columna nueva: ascendente. Sobre la ya ordenada: alterna. Vuelve a la
   * página 1, porque la fila que estabas viendo casi seguro cambió de página.
   */
  const requestSort = (key) => {
    const anterior = sortQuery[key];
    const query = { ...sortQuery };
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      query[k] = '';
    });
    query[key] = anterior === 'asc' ? 'desc' : 'asc';

    // Ordenar por una columna que no es la del grupo desarma la agrupación: los bloques dejarían de
    // ser bloques (sus filas quedarían intercaladas) y seguir dibujando los separadores sería
    // mentir. Si es la MISMA columna, la agrupación sobrevive: solo se invirtió el orden dentro.
    const config = AGRUPACIONES[agrupacion];
    if (config && config.campo !== key) {
      setAgrupacion('ninguna');
      setGruposAbiertos([]);
    }

    setPagination((prev) => ({ ...prev, page: 0 }));
    setSortQuery(query);
  };

  /**
   * Cambiar de pestaña vuelve a la página 1 y CONSERVA los filtros: si estabas mirando el depósito
   * central, querés ver sus finalizadas, no las de todos. Lo que no conserva es la página, que en la
   * otra vista puede no existir.
   */
  const handleVista = (nueva) => {
    if (nueva === vista) return;
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSeleccionados([]);
    // El orden vuelve al de la pestaña a la que se entra. Ver `ordenPorDefecto`: las columnas por las
    // que ordena cada una no existen en la otra.
    setSortQuery(ordenPorDefecto(nueva));
    setAgrupacion('ninguna');
    setGruposAbiertos([]);
    setVista(nueva);
  };

  // LIMPIAR LA BÚSQUEDA — la "✕" de la franja de resultados: filtros al default y franja oculta.
  // NO cambia de pestaña: la vista no es un filtro.
  const handleSearchClear = () => {
    setPagination({ ...PAGINATION_INIT });
    setSeleccionados([]);
    setFilterQuery(buildFilterQueryInit());
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  // RESET TOTAL — el botón "Restaurar" del toolbar: además de limpiar la búsqueda, devuelve el
  // ordenamiento por defecto y cierra el panel. Reusa el parcial para no duplicar el reset; React
  // batchea los setState del handler, así que sigue habiendo un solo fetch.
  // Ver docs/design-system/grid-filter-panel.md ("Dos resets, no uno").
  const handleFilterReset = () => {
    setSortQuery(ordenPorDefecto(vista));
    setAgrupacion('ninguna');
    setGruposAbiertos([]);
    columnas.mostrarTodas();
    setDropdownActive(false);
    handleSearchClear();
  };

  /**
   * La franja se muestra si el query DIFIERE del default. Acá el default es vacío, así que equivale a
   * "hay algún campo cargado"; se compara igual contra el default para que la regla no cambie si
   * algún día la pantalla abre con un período elegido.
   */
  const handleApplyFilters = (query) => {
    const porDefecto = buildFilterQueryInit();
    const difiere = Object.keys(query).some((k) => (query[k] || '') !== (porDefecto[k] || ''));

    setPagination((prev) => ({ ...prev, page: 0 }));
    setSeleccionados([]);
    setFilterQuery(query);
    setSearchActive(difiere);
  };

  /**
   * Una pestaña. Es un `button` con `role="tab"` y no el `<a>` sin `href` que usan las pestañas más
   * viejas del repo (`TicketMovilMantenimientos`): un ancla sin destino no es alcanzable por teclado
   * ni se anuncia como control, así que la pantalla quedaría sin forma de cambiar de vista sin
   * mouse. Las clases de Bootstrap son las mismas, van por clase y no por etiqueta.
   *
   * El único ajuste es el fondo del botón INACTIVO: `.nav-tabs .nav-link` de Bootstrap ya resetea el
   * borde (`1px solid transparent`, bootstrap.css:2660) pero no el fondo, y el del user-agent para un
   * `button` es gris. Va como ternario y no fijo: con `background: none` a secas se pisaba también el
   * `#F9FAFD` y el `border-color` que `.active` usa para marcar la pestaña elegida, y las dos se veían
   * iguales. `undefined` no emite la propiedad, así que en la activa manda el CSS.
   */
  const etiquetaDespachar = intl.formatMessage({
    id: 'inventarioSolicitudes.grid.despachar',
    defaultMessage: 'Despachar',
  });

  const etiquetaCancelar = intl.formatMessage({
    id: 'inventarioSolicitudes.grid.rechazar_solicitud',
    defaultMessage: 'Rechazar',
  });

  const etiquetaGestiones = intl.formatMessage({
    id: 'inventarioSolicitudes.grid.ver_gestiones',
    defaultMessage: 'Ver cómo se gestionó',
  });

  const etiquetaSeleccionarTodo = intl.formatMessage({
    id: 'inventarioSolicitudes.grid.seleccionar_todo',
    defaultMessage: 'Seleccionar todo lo visible',
  });

  const etiquetaAgrupar = intl.formatMessage({
    id: 'inventarioSolicitudes.grid.agrupar',
    defaultMessage: 'Agrupar:',
  });

  /**
   * El color de un modo de agrupación: el que toma la píldora al elegirlo.
   *
   * Lo usan los DOS lados —la píldora y la franja izquierda de cada opción del menú— justamente para
   * que se correspondan: así la franja anticipa de qué color se va a teñir el selector, que es lo
   * que hace el menú de Estado de la grilla de Vehículos con los colores de cada estado.
   *
   * Depende del MODO y no de cuál está vigente: si dependiera de lo vigente, todas las opciones no
   * elegidas se verían iguales y la franja no diría nada.
   */
  const colorDeAgrupacion = (modo) => (modo === 'ninguna' ? COLOR_NEUTRO : COLOR_ENCABEZADO);

  /**
   * Cómo se llama lo que cuenta el pie: con agrupación son GRUPOS, sin ella son solicitudes.
   *
   * Hace falta decirlo porque el número cambia de significado: agrupando por ticket, "de 3" son 3
   * tickets aunque en pantalla haya 24 filas. Sin esta palabra el pie parece estar roto.
   */
  const unidadPaginada = () => {
    const unidades = {
      ninguna: intl.formatMessage({ id: 'inventarioSolicitudes.grid.unidad_solicitudes', defaultMessage: 'solicitudes' }),
      ticket: intl.formatMessage({ id: 'inventarioSolicitudes.grid.unidad_tickets', defaultMessage: 'tickets' }),
      item: intl.formatMessage({ id: 'inventarioSolicitudes.grid.unidad_repuestos', defaultMessage: 'repuestos' }),
      deposito: intl.formatMessage({ id: 'inventarioSolicitudes.grid.unidad_depositos', defaultMessage: 'depósitos' }),
    };
    return unidades[agrupacion] || unidades.ninguna;
  };

  /** El texto que se ve junto al ícono: qué agrupación está activa. */
  const etiquetaAgrupacionActiva = () => {
    const etiquetas = {
      ninguna: intl.formatMessage({ id: 'inventarioSolicitudes.grid.agrupar_no', defaultMessage: 'Sin agrupar' }),
      ticket: intl.formatMessage({ id: 'inventarioSolicitudes.grid.agrupar_ticket_corto', defaultMessage: 'Por ticket' }),
      item: intl.formatMessage({ id: 'inventarioSolicitudes.grid.agrupar_item_corto', defaultMessage: 'Por repuesto' }),
      deposito: intl.formatMessage({ id: 'inventarioSolicitudes.grid.agrupar_deposito_corto', defaultMessage: 'Por depósito' }),
    };
    return etiquetas[agrupacion] || etiquetas.ninguna;
  };

  /**
   * Una opción del menú. Es un `<p className="dropdown-item">` y no un `<button>` porque así lo hace
   * el selector de Estado de Vehículos, que es el molde que pidió el PO; el `active` de Bootstrap
   * marca la vigente.
   */
  /**
   * Una opción del menú.
   *
   * `<button>` y no el `<p>` del selector de Estado de Vehículos, que es el molde visual: un `<p>`
   * con `onClick` no recibe foco ni se activa con teclado, así que la agrupación quedaba inaccesible
   * para quien no usa mouse (lo marcó la review del PR #2346). `dropdown-item` sobre un `button` es
   * uso estándar de Bootstrap y se ve igual; lo único que hace falta es resetear lo que el navegador
   * le pone de fábrica a un botón (ancho, alineación, borde y fondo).
   */
  const renderAgrupacion = (modo, messageId, label) => (
    <button
      type="button"
      key={modo}
      className="cursor-pointer dropdown-item"
      style={{
        margin: 0,
        padding: '5px 5px',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        borderRadius: 0,
        background: 'none',
        // La franja lleva el color de SU modo, no el de la selección: anticipa de qué color va a
        // quedar el selector si se elige esa opción. Cuál está vigente se marca con la negrita.
        borderLeft: 'solid ' + colorDeAgrupacion(modo),
        fontWeight: agrupacion === modo ? 600 : 400,
      }}
      onClick={() => handleAgrupacion(modo)}
    >
      <FormattedMessage id={messageId} defaultMessage={label} />
    </button>
  );

  // ---------------------------------------------------------------------------------------------
  // Selección masiva
  // ---------------------------------------------------------------------------------------------

  /**
   * Después de gestionar: cerrar el modal y recargar.
   *
   * Se recarga del servidor en vez de parchear la fila en memoria: el despacho puede haber cerrado
   * la línea, y entonces ya no pertenece a la pestaña que se está viendo. Parchear dejaría una fila
   * FINALIZADA en la vista de Pendientes hasta el próximo fetch.
   *
   * También se saca de la selección: si quedó tildada y ya no se puede gestionar, la barra contaría
   * algo sobre lo que no se puede actuar.
   */
  const handleGestionada = (detalleId) => {
    setADespachar(null);
    setACancelar(null);
    setSeleccionados((prev) => prev.filter((id) => id !== detalleId));
    setRecarga((n) => n + 1);
  };

  /**
   * Después de una acción masiva: sacar de la selección solo lo que efectivamente se procesó y
   * recargar.
   *
   * Lo que falló se deja tildado a propósito: es la lista de pendientes para reintentar, y limpiar
   * todo obligaría a volver a buscarlas una por una. El refetch corre igual porque lo que sí salió
   * ya cambió de estado.
   */
  const handleMasivaProcesada = (idsOk) => {
    setSeleccionados((prev) => prev.filter((id) => idsOk.indexOf(id) === -1));
    setRecarga((n) => n + 1);
  };

  const estaSeleccionado = (id) => seleccionados.indexOf(id) !== -1;

  /**
   * Las filas tildadas, como objetos.
   *
   * Hace falta un caché porque la selección NO se limpia al paginar (ver arriba) y `rows` solo tiene
   * la página actual: sin esto, una acción masiva sobre una selección de dos páginas procesaría
   * calladamente la mitad. Acumula por id y se vacía junto con la selección, que es cuando los ids
   * dejan de significar algo.
   */
  const [cacheFilas, setCacheFilas] = useState({});

  useEffect(() => {
    if (rows.length === 0) return;
    setCacheFilas((prev) => {
      const siguiente = Object.assign({}, prev);
      rows.forEach((r) => {
        siguiente[r.id] = r;
      });
      return siguiente;
    });
  }, [rows]);

  // Solo las que todavía tienen algo que entregar: sobre una línea sin pendiente no hay nada que
  // despachar ni que rechazar, y el backend la rechazaría de a una.
  const filasSeleccionadas = () =>
    seleccionados.map((id) => cacheFilas[id]).filter((r) => r && Number(r.cantidadPendiente) > 0);

  const alternarFila = (id) => {
    setSeleccionados((prev) => (prev.indexOf(id) === -1 ? prev.concat([id]) : prev.filter((x) => x !== id)));
  };

  /**
   * Tilda o destilda un conjunto entero (la página, o un grupo).
   *
   * La regla es "si YA está todo tildado, destildar; si no, tildar todo": con una selección parcial
   * el click completa en vez de vaciar, que es lo que uno espera de una casilla a medias.
   */
  const alternarConjunto = (ids) => {
    const todos = ids.length > 0 && ids.every((id) => estaSeleccionado(id));
    setSeleccionados((prev) =>
      todos ? prev.filter((x) => ids.indexOf(x) === -1) : prev.concat(ids.filter((id) => prev.indexOf(id) === -1))
    );
  };

  /**
   * Casilla de tres estados. `indeterminate` no es un atributo del HTML —solo existe como propiedad
   * del nodo—, así que se setea por ref en cada render; con `checked` solo, una selección parcial se
   * vería idéntica a una vacía.
   */
  const renderCheck = (ids, etiqueta) => {
    const total = ids.length;
    const tildados = ids.filter((id) => estaSeleccionado(id)).length;
    return (
      <input
        type="checkbox"
        className="customCheckbox"
        aria-label={etiqueta}
        disabled={total === 0}
        checked={total > 0 && tildados === total}
        ref={(el) => {
          if (el) el.indeterminate = tildados > 0 && tildados < total;
        }}
        onChange={() => alternarConjunto(ids)}
      />
    );
  };

  const estaAbierto = (clave) => gruposAbiertos.indexOf(String(clave)) !== -1;

  const alternarGrupo = (clave) => {
    const k = String(clave);
    setGruposAbiertos((prev) => (prev.indexOf(k) === -1 ? prev.concat([k]) : prev.filter((x) => x !== k)));
  };

  /**
   * Las filas de la página partidas en bloques, cortando cada vez que cambia la clave del grupo.
   *
   * El corte se calcula sobre las filas YA ORDENADAS por el servidor: acá no se reordena nada, solo
   * se mira dónde cambia el valor respecto de la anterior. Por eso la agrupación necesita que el
   * orden lo haya aplicado la consulta —si no, las filas del mismo grupo no serían contiguas y
   * saldrían varios bloques con el mismo título—.
   *
   * Un bloque nunca queda partido entre páginas: cuando hay agrupación el backend pagina por grupo
   * y manda el grupo entero (ver el mapa de AGRUPACIONES). Por eso el conteo del bloque es el total
   * real del ticket y no "lo que entró en esta página".
   */
  const agruparFilas = () => {
    const config = AGRUPACIONES[agrupacion];
    if (!config) return [{ clave: null, filas: rows }];

    const bloques = [];
    rows.forEach((row) => {
      const clave = config.clave(row);
      const ultimo = bloques[bloques.length - 1];
      if (!ultimo || ultimo.clave !== clave) {
        bloques.push({ clave: clave, titulo: config.titulo(row, intl), filas: [row] });
      } else {
        ultimo.filas.push(row);
      }
    });
    return bloques;
  };

  /**
   * Los nodos del `tbody`: por cada bloque su cabecera desplegable y, si está abierto, sus filas.
   *
   * Los bloques arrancan CERRADOS y se abren con un click. Colapsar no trae menos datos, solo los
   * oculta: el servidor ya mandó todas las filas de los grupos de la página.
   */
  const filasConGrupos = () => {
    const cantidadColumnas = columnasVisibles(columnas.esVisible);
    const nodos = [];

    agruparFilas().forEach((bloque) => {
      if (bloque.clave !== null) {
        const abierto = estaAbierto(bloque.clave);
        nodos.push(
          <TableRow key={'Grupo_' + bloque.clave}>
            {/* El check del grupo va en SU PROPIA celda, alineado con la columna de selección: si
                viviera dentro de la celda del título quedaría corrido y, sobre todo, su click
                dispararía también el del título y abriría o cerraría el bloque. */}
            <StyledTableCell
              align="center"
              style={{ backgroundColor: '#f4f5fa', borderTop: '1px solid #e3ebf3', width: 40, padding: '12px 8px' }}
            >
              {renderCheck(bloque.filas.map((r) => r.id), bloque.titulo)}
            </StyledTableCell>
            <StyledTableCell
              colSpan={cantidadColumnas - 1}
              style={{
                backgroundColor: '#f4f5fa',
                color: COLOR_ENCABEZADO,
                fontWeight: 600,
                borderTop: '1px solid #e3ebf3',
                whiteSpace: 'normal',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() => alternarGrupo(bloque.clave)}
            >
              {/* El `div` con role/aria lleva la semántica; el click está en la celda entera para
                  que se pueda apretar en cualquier lado de la fila, que es lo que uno intenta. */}
              <div
                role="button"
                tabIndex={0}
                aria-expanded={abierto}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    alternarGrupo(bloque.clave);
                  }
                }}
                style={{ display: 'inline-flex', alignItems: 'center', outlineOffset: 2 }}
              >
                {/* La doble flecha de la grilla de Inspecciones de Llantas
                    (LlantaInspeccionesGrid.js:123), que es el ícono con el que el usuario ya
                    reconoce una fila desplegable en VEC. Sin el `fa-2x` de allá: ahí encabeza una
                    fila entera y acá acompaña a un texto. */}
                <i
                  className={'fa ' + (abierto ? 'fa-angle-double-down' : 'fa-angle-double-right')}
                  aria-hidden="true"
                  style={{ fontSize: 15, marginRight: 8, width: 12, textAlign: 'center' }}
                ></i>
                {bloque.titulo}
                {/* El conteo no es decorativo: un bloque cerrado sin él no dice cuánto esconde. */}
                <span style={{ fontWeight: 400, color: COLOR_NEUTRO, marginLeft: 8 }}>
                  {intl.formatMessage(
                    {
                      id: 'inventarioSolicitudes.grid.grupo_cantidad',
                      defaultMessage: '{n, plural, one {# repuesto} other {# repuestos}}',
                    },
                    { n: bloque.filas.length }
                  )}
                </span>
              </div>
            </StyledTableCell>
          </TableRow>
        );

        if (!abierto) return;
      }

      bloque.filas.forEach((row) => {
        nodos.push(
          <InventarioSolicitudesGridRow
            key={'GridRow_' + row.id}
            row={row}
            visibles={columnas.visibles}
            seleccionado={estaSeleccionado(row.id)}
            onSeleccionar={alternarFila}
            labelDespachar={etiquetaDespachar}
            labelCancelar={etiquetaCancelar}
            labelGestiones={etiquetaGestiones}
            onVerGestiones={setAVerGestiones}
            onDespachar={setADespachar}
            onCancelar={setACancelar}
          />
        );
      });
    });

    return nodos;
  };

  const renderTab = (valor, messageId, label) => (
    <li className="nav-item" style={SOLAPA_ITEM}>
      <button
        type="button"
        role="tab"
        aria-selected={vista === valor}
        className={'nav-link' + (vista === valor ? ' active' : '')}
        style={vista === valor ? SOLAPA_ACTIVA : SOLAPA_INACTIVA}
        // El anillo de foco del navegador dibuja un recuadro alrededor del botón al clickearlo, y
        // eso rompe la ilusión de solapa. Se evita impidiendo que el click DÉ FOCO —el
        // `preventDefault` del mousedown cancela esa acción por defecto— en vez de matar el
        // `outline` por CSS: apagarlo dejaría también sin indicador a quien navega con Tab, que es
        // justamente para quien se eligió un `button` y no el `<a>` sin href de las solapas viejas.
        // El foco por teclado sigue intacto, anillo incluido.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleVista(valor)}
      >
        <FormattedMessage id={messageId} defaultMessage={label} />
      </button>
    </li>
  );

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  {/* Tres `col` de igual ancho: con col-6 + col + col-6 se pasa de las 12 columnas
                      de Bootstrap y el bloque de la derecha se va a la línea siguiente. */}
                  <div className="row dt-icons">
                    <div className="col">
                      {/* Sin botón "Nuevo": una solicitud no se crea desde acá, la genera un ticket
                          (VEC-3319). Esta pantalla solo lee. */}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    <div className="col">
                      {/* `mostrados` va en la misma unidad que `total`: con agrupación el total son
                          grupos, así que contar filas acá diría "24 de 3". */}
                      <GridSearchResultsBadge
                        visible={searchActive}
                        mostrados={AGRUPACIONES[agrupacion] ? agruparFilas().length : rows.length}
                        total={count}
                        onReset={() => handleSearchClear()}
                      />
                    </div>
                    <div className="col">
                      <GridToolbar>
                        <GridColumnSelector
                          columns={columnas.seleccionables}
                          esVisible={columnas.esVisible}
                          onToggle={columnas.toggle}
                        />
                        <GridToolbarButton
                          messageId="inventarioSolicitudes.grid.tools_restore_filters.title"
                          label="Restaurar filtros"
                          onClick={() => handleFilterReset()}
                        >
                          <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                        </GridToolbarButton>
                      </GridToolbar>
                    </div>
                  </div>
                </div>

                <InventarioSolicitudesGridFiltros
                  open={dropdownActive}
                  loading={loading}
                  resetSignal={filterResetInputs}
                  onResetSignalHandled={() => setFilterResetInputs(false)}
                  onApply={handleApplyFilters}
                />

                {/* Las dos vistas del submódulo, encabezando la tabla como solapas: la activa toma
                    el color del encabezado y se funde con él.
                    NO van arriba de la card: el botón redondo que abre los filtros es un
                    `.round-icon`, que está posicionado con `top: -45px` (vec-datatables.css:177) y
                    se monta sobre lo que tenga encima — le robaba los clicks a la pestaña, que se
                    veía bien pero no se podía apretar. Acá abajo no lo alcanza. */}
                <ul
                  // `mt-4` no es estético: el botón redondo que abre los filtros es un `.round-icon`
                  // posicionado con `top: -45px` (vec-datatables.css:177) y su columna del toolbar se
                  // superpone con lo que venga inmediatamente después, robándole los clicks. Hasta
                  // VEC-3318 esa separación la daba el aviso de "pantalla de consulta", que se quitó
                  // al llegar las acciones de verdad; sin reemplazarla, las solapas dejaban de
                  // responder. Lo fija el caso e2e de cambio de pestaña.
                  className="nav nav-tabs mb-0 mt-4"
                  role="tablist"
                  // Dos cosas:
                  //
                  // 1. Sin la línea gris que Bootstrap le pone a `.nav-tabs`: acá abajo no hay un
                  //    panel claro que necesite cerrarse con un borde, hay un encabezado oscuro. La
                  //    línea cortaría justo entre la solapa activa y la tabla, que es la unión que
                  //    se busca.
                  //
                  // 2. Sin `z-index` ni `position` acá. Se probó bajar las solapas por debajo de la
                  //    tabla para tapar el píxel de más (ver el punto 3), pero eso convierte a este
                  //    `<ul>` en un CONTEXTO DE APILADO y encierra a sus descendientes: el menú de
                  //    agrupación quedaba dibujado debajo de la tabla y sus opciones eran
                  //    inclickeables, sin que subirle el z-index al menú lo arreglara. El desnivel
                  //    se ataca en el margen, que es donde se origina.
                  //
                  // 3. `marginBottom: 0` en cada `.nav-item` (ver SOLAPA_ITEM): Bootstrap les pone
                  //    `-1px` para que la solapa se monte sobre el panel de abajo y tape su borde.
                  //    Acá abajo no hay borde que tapar sino un encabezado oscuro, así que ese píxel
                  //    solo lograba que el gris claro de la solapa inactiva se dibujara encima y se
                  //    viera un desnivel. Sin solape, las dos piezas quedan al ras.
                  style={{ borderBottom: 'none' }}
                >
                  {renderTab(VISTA_PENDIENTES, 'inventarioSolicitudes.grid.tab_pendientes', 'Pendientes')}
                  {renderTab(VISTA_FINALIZADAS, 'inventarioSolicitudes.grid.tab_finalizadas', 'Finalizadas')}

                  {/* La agrupación va acá adentro, empujada a la derecha con `ml-auto`, para que
                      comparta la línea base con las solapas sin necesidad de otro contenedor.
                      Ícono + `dropdown-menu` de Bootstrap, que es el molde del selector de Estado de
                      la grilla de Vehículos (MovilesGrid.jsx, encabezado de la columna Estado): lo
                      abre el `data-toggle`, no un onClick nuestro. */}
                  <li className="nav-item ml-auto d-flex align-items-center" style={{ ...SOLAPA_ITEM, paddingBottom: 6 }}>
                    <div className="dropdown">
                      {/* La píldora con el criterio vigente y el chevron que abre el menú. Copia del
                          selector de Estado de la grilla de Vehículos (MovilesGridRow.jsx, celda de
                          `movil.estado`): mismo `borderRadius: 7px`, mismo relleno, texto blanco y el
                          `fa fa-chevron-down` adentro con el `data-toggle` de Bootstrap. */}
                      {/* `<button>` nativo y no un `div` con `role="button"`: así Enter y Espacio lo
                          activan solos. Con el div, quien navega con teclado llegaba al control pero
                          no podía abrirlo —Bootstrap no suple la activación de un no-botón—; lo marcó
                          la review del PR #2346. Los estilos de la píldora se mantienen porque el
                          fondo, el borde y el color van explícitos acá. */}
                      <button
                        type="button"
                        id="agruparLabel"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        aria-label={etiquetaAgrupar}
                        className="d-flex align-items-center cursor-pointer"
                        style={{
                          backgroundColor: colorDeAgrupacion(agrupacion),
                          // Arriba/abajo/derecha los 4px del original; a la IZQUIERDA 8, porque ahí
                          // el texto arranca pegado al borde mientras que del otro lado el chevron
                          // ya trae 5px de margen propio. Con 4 parejos se ve descentrado.
                          padding: '4px 4px 4px 8px',
                          borderRadius: '7px',
                          color: 'white',
                          // 11 y no los 9 del original: allá la píldora vive dentro de una celda
                          // apretada y acá es un control suelto a la altura de las solapas.
                          fontSize: '11px',
                          backgroundSize: 'auto',
                          width: 'fit-content',
                          border: '1px solid',
                        }}
                      >
                        <div>{etiquetaAgrupacionActiva()}</div>
                        {/* Solo decorativo: quien abre el menú es la píldora entera. */}
                        <i
                          className="fa fa-chevron-down"
                          aria-hidden="true"
                          style={{ marginRight: '5px', marginLeft: '5px' }}
                        ></i>
                      </button>

                      {/* HERMANO del disparador, no hijo. En la celda de Vehículos el menú vive
                          DENTRO de la píldora porque ahí el disparador es solo el chevron; acá, con
                          toda la píldora clickeable, un menú adentro haría que el click en una
                          opción burbujee al disparador y vuelva a abrirlo. Esta es además la
                          estructura canónica de Bootstrap: `.dropdown > [data-toggle] + .dropdown-menu`. */}
                      <div className="dropdown-menu dropdown-menu-right" aria-labelledby="agruparLabel">
                        {renderAgrupacion('ninguna', 'inventarioSolicitudes.grid.agrupar_no', 'Sin agrupar')}
                        {renderAgrupacion('ticket', 'inventarioSolicitudes.grid.agrupar_ticket', 'Agrupar por ticket')}
                        {renderAgrupacion('item', 'inventarioSolicitudes.grid.agrupar_item', 'Agrupar por repuesto')}
                        {renderAgrupacion('deposito', 'inventarioSolicitudes.grid.agrupar_deposito', 'Agrupar por depósito')}
                      </div>
                    </div>
                  </li>
                </ul>

                <TableContainer>
                  <Table aria-label="inventario solicitudes table" className="table-hover">
                    <TableHead>
                      <TableRow>
                        {/* Selección masiva de todo lo visible en la página. No es ordenable ni
                            ocultable. */}
                        <StyledTableCell align="center" style={{ width: 40, padding: '12px 8px' }}>
                          {renderCheck(rows.map((r) => r.id), etiquetaSeleccionarTodo)}
                        </StyledTableCell>

                        {/* Todas las columnas de datos son ordenables. La de acciones va sin
                            `onSort`: sin esa prop SortableCell no pone cursor ni icono. */}
                        <SortableCell
                          field="id"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('id')}
                          messageId="inventarioSolicitudes.grid.column_id"
                          label="ID"
                        />
                        <SortableCell
                          field="fecha"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('fecha')}
                          messageId="inventarioSolicitudes.grid.column_fecha"
                          label="Fecha"
                        />
                        <SortableCell
                          field="ticket"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('ticket')}
                          messageId="inventarioSolicitudes.grid.column_ticket"
                          label="Ticket"
                        />
                        <SortableCell
                          field="numeroDeParte"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('numeroDeParte')}
                          messageId="inventarioSolicitudes.grid.column_numero_de_parte"
                          label="Nº de parte"
                        />
                        <SortableCell
                          field="descripcion"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('descripcion')}
                          messageId="inventarioSolicitudes.grid.column_item"
                          label="Repuesto"
                        />
                        <SortableCell
                          field="deposito"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('deposito')}
                          messageId="inventarioSolicitudes.grid.column_deposito"
                          label="Depósito"
                        />
                        <SortableCell
                          field="disponible"
                          sort={sortQuery}
                          onSort={requestSort}
                          align="center"
                          show={columnas.esVisible('disponible')}
                          messageId="inventarioSolicitudes.grid.column_disponible"
                          label="Disponible"
                        />
                        <SortableCell
                          field="cantidad"
                          sort={sortQuery}
                          onSort={requestSort}
                          align="center"
                          show={columnas.esVisible('cantidad')}
                          messageId="inventarioSolicitudes.grid.column_cantidad"
                          label="Solicitado"
                        />
                        <SortableCell
                          field="despachado"
                          sort={sortQuery}
                          onSort={requestSort}
                          align="center"
                          show={columnas.esVisible('despachado')}
                          messageId="inventarioSolicitudes.grid.column_despachado"
                          label="Despachado"
                        />
                        <SortableCell
                          field="pendiente"
                          sort={sortQuery}
                          onSort={requestSort}
                          align="center"
                          show={columnas.esVisible('pendiente')}
                          messageId="inventarioSolicitudes.grid.column_pendiente"
                          label="Pendiente"
                        />
                        <SortableCell
                          field="fechaCierre"
                          sort={sortQuery}
                          onSort={requestSort}
                          align="center"
                          show={columnas.esVisible('fechaCierre')}
                          messageId="inventarioSolicitudes.grid.column_fecha_cierre"
                          label="Fecha de cierre"
                        />
                        <SortableCell
                          field="solicitante"
                          sort={sortQuery}
                          onSort={requestSort}
                          show={columnas.esVisible('solicitante')}
                          messageId="inventarioSolicitudes.grid.column_solicitante"
                          label="Solicitante"
                        />
                        <SortableCell
                          align="center"
                          messageId="inventarioSolicitudes.grid.column_acciones"
                          label="Acciones"
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <GridLoadingRow visible={loading} columns={columnasVisibles(columnas.esVisible)} />
                      {!loading &&
                        (rows.length > 0 ? (
                          filasConGrupos()
                        ) : (
                          <GridEmptyRow
                            columns={columnasVisibles(columnas.esVisible)}
                            messageId="inventarioSolicitudes.grid.no_data_table"
                            label="No se encontraron solicitudes con los filtros seleccionados."
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Barra de acciones masivas. Molde: FormularioGrid.jsx (histórico de checklist).
                    Aparece sólo con algo tildado, así no ocupa lugar el resto del tiempo.

                    Los botones de acción salen únicamente si hay algo sobre lo que accionar: con una
                    selección hecha solo de líneas ya cerradas queda el contador y limpiar, sin
                    botones mudos (deshabilitado en MUI no muestra tooltip y no explica nada).

                    El despacho masivo es SOLO POR COMPRA; el porqué está en el modal. */}
                {seleccionados.length > 0 && (
                  <div className="d-flex align-items-center border rounded p-2 mt-2 mb-2 bg-light">
                    <strong className="mr-auto">
                      <FormattedMessage
                        id="inventarioSolicitudes.grid.seleccion_cantidad"
                        defaultMessage="{n, plural, one {# solicitud seleccionada} other {# solicitudes seleccionadas}}"
                        values={{ n: seleccionados.length }}
                      />
                    </strong>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setSeleccionados([]);
                        setCacheFilas({});
                      }}
                    >
                      <FormattedMessage
                        id="inventarioSolicitudes.grid.seleccion_cancelar"
                        defaultMessage="Cancelar selección"
                      />
                    </button>

                    {filasSeleccionadas().length > 0 &&
                      Security.hasPermission('INVENTARIO_SOLICITUDES_DESPACHAR') && (
                        <button
                          type="button"
                          className="btn btn-sm btn-fleet ml-2"
                          onClick={() => setAccionMasiva(ACCION_DESPACHAR)}
                        >
                          <i className="fa fa-truck mr-1" />
                          <FormattedMessage
                            id="inventarioSolicitudes.grid.seleccion_despachar"
                            defaultMessage="Despachar por compra"
                          />
                        </button>
                      )}

                    {filasSeleccionadas().length > 0 &&
                      Security.hasPermission('INVENTARIO_SOLICITUDES_CANCELAR') && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger ml-2"
                          onClick={() => setAccionMasiva(ACCION_RECHAZAR)}
                        >
                          <i className="fa fa-thumbs-down mr-1" />
                          <FormattedMessage
                            id="inventarioSolicitudes.grid.seleccion_rechazar"
                            defaultMessage="Rechazar"
                          />
                        </button>
                      )}
                  </div>
                )}

                {/* `labelDisplayedRows` nombra la unidad: agrupando, el pie cuenta GRUPOS y no
                    filas, y "Mostrando 1 a 2 de 3" sobre una pantalla con 20 filas se lee como un
                    bug si no dice de qué son esos 3. */}
                <GridPagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={count}
                  page={pagination.page}
                  perPage={pagination.perPage}
                  onPageChange={handleChangePage}
                  onPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count: total }) =>
                    intl.formatMessage(
                      {
                        id: 'inventarioSolicitudes.grid.paginacion',
                        defaultMessage: 'Mostrando {from} a {to} de {total} {unidad}',
                      },
                      { from: from, to: to, total: total, unidad: unidadPaginada() }
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InventarioSolicitudDespachoModal
        detalle={aDespachar}
        onClose={() => setADespachar(null)}
        onDespachado={() => handleGestionada(aDespachar ? aDespachar.id : null)}
      />

      <InventarioSolicitudCancelarModal
        detalle={aCancelar}
        onClose={() => setACancelar(null)}
        onCancelado={() => handleGestionada(aCancelar ? aCancelar.id : null)}
      />

      <InventarioSolicitudGestionesModal
        detalle={aVerGestiones}
        onClose={() => setAVerGestiones(null)}
      />

      <InventarioSolicitudesMasivaModal
        accion={accionMasiva}
        detalles={filasSeleccionadas()}
        onClose={() => setAccionMasiva(null)}
        onProcesado={handleMasivaProcesada}
      />
    </>
  );
};

export default InventarioSolicitudesGrid;
